import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';

// constants
import appRoutes from '../../../../app-routes';
import { QueryKeys } from '../../../../queryKeys';
import { NOTIFICATION_MODULE_MENU_LABEL } from '../../../../constraints';

// hooks
import useFilters from '../../../../hooks/useFilters';
import { usePrivateSingleNotificationGroupContent } from '../../hooks/privateNotifications/usePrivateSingleNotificationGroupContent';

// Components
import Modal from '../../components/Modal';
import { ErrorModal } from '../../../../components/ErrorModal';
import { SuccessModal } from '../../../../components/SuccessModal';
import { ArchiveForm } from '../../components/Templates/components/NotificationManager/components';

import { Layout, Utils } from 'billon-ui';
const { Content: ContentModule } = Layout;
const { ErrorContent } = ContentModule;
const { Loader: LoaderModule } = Utils;
const { PageLoader } = LoaderModule;

const ArchiveTemplatePage = () => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { filters, navigateWithNewSearchQuery } = useFilters();

  const { contentId } = filters?.filters || {};
  const { data: contentData, isFetching: isContentLoading } =
    usePrivateSingleNotificationGroupContent(contentId, {
      enabled: !!contentId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const modalTitle = formatMessage({
    id: NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE_MODAL,
    defaultMessage: NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE_MODAL,
  });

  const modalSuccess = formatMessage({
    id: NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE_SUCCESS,
    defaultMessage: NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE_SUCCESS,
  });

  const modalError = formatMessage({
    id: NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE_ERROR,
    defaultMessage: NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE_ERROR,
  });

  const [createModalOpened, setCreateModalOpened] = useState(true);
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [errorContent, setErrorContent] = useState(null);
  const [successModalOpened, setSuccessModalOpened] = useState(false);

  const handleCreateModalClose = () => {
    setCreateModalOpened(false);
    const { contentId, ...rest } = filters?.filters;
    navigateWithNewSearchQuery(
      { filters: { ...rest } },
      appRoutes.NOTIFICATION_TEMPLATE_MANAGE,
    );
  };
  const handleSuccess = (groupContentId) => {
    setSuccessModalOpened(true);
    if (!!groupContentId) {
      queryClient.invalidateQueries([
        QueryKeys.PRIVATE_SINGLE_NOTIFICATION_GROUP_CONTENT,
        groupContentId,
      ]);
    }
  };
  const handleError = () => {
    setErrorModalOpened(true);
    queryClient.invalidateQueries([
      QueryKeys.PRIVATE_NOTIFICATION_GROUP_CONTENTS,
      filters?.filters?.groupId,
    ]);
  };
  const handleErrorModalClose = () => {
    setErrorModalOpened(false);
    setErrorContent(null);
  };
  const handleSuccessModalClose = () => {
    setSuccessModalOpened(false);
    queryClient.invalidateQueries([
      QueryKeys.PRIVATE_NOTIFICATION_GROUP_CONTENTS,
      filters?.filters?.groupId,
    ]);
    handleCreateModalClose();
  };

  if (isContentLoading) {
    return (
      <Modal isOpen={true} title={modalTitle}>
        <PageLoader
          message={
            <FormattedMessage
              id="Contents are loading..."
              defaultMessage="Contents are loading..."
            />
          }
        />
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={createModalOpened}
        toggle={handleCreateModalClose}
        onClose={handleCreateModalClose}
        title={modalTitle}
      >
        <ArchiveForm
          submitButtonText={NOTIFICATION_MODULE_MENU_LABEL.TEMPLATE_ARCHIVE}
          handleSuccess={handleSuccess}
          handleError={handleError}
          groupId={filters?.filters?.groupId}
          contentData={contentData}
        />
      </Modal>
      <ErrorModal
        isOpen={errorModalOpened}
        toggle={handleErrorModalClose}
        onClose={handleErrorModalClose}
        closeIconEnabled={false}
      >
        <ErrorContent>
          {modalError}
          {errorContent}
        </ErrorContent>
      </ErrorModal>
      <SuccessModal
        isOpen={successModalOpened}
        toggle={handleSuccessModalClose}
        onClose={handleSuccessModalClose}
        closeIconEnabled={false}
      >
        {modalSuccess}
      </SuccessModal>
    </>
  );
};

export default ArchiveTemplatePage;
